import React, { useContext, useEffect, useState, useMemo } from "react";
import * as Sentry from "@sentry/react";
import { UI } from "@wwimmo/ui";
import { RootStoreContext } from "src/stores/RootStore";
import { Route, selectRoute } from "src/config/routes";
import { useHistory } from "react-router-dom";
import { Step } from "src/screens/process/step/Step";
import { useCallback } from "react";
import { BrandLogo } from "src/components/brandLogo/BrandLogo";
import { toJS } from "mobx";
import { useRef } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Disclaimers } from "./Disclaimers";
import { NetworkConfig } from "src/network/NetworkConfig";
import { useUrlQuery } from "src/utils/Route";
import { cloneDeep, omit } from "lodash";
import { hashCode } from "src/utils/Hash";
import { getLang } from "src/utils/i18n";
import { DIRECT_ROUTING_PREFIX } from "src/screens/service/Service";
import { observer } from "mobx-react";
import { CONNECTION_TYPES, NOTIFICATION_TYPES } from "src/api/Notification";
import { COOKIE_PROPERTY_IS_LOGGED_IN, COOKIE_PROPERTY_REFRESH_TOKEN } from "src/screens/auth/login/Login";

interface UploadErrorListType {
    text: any;
}

enum STATE {
    INIT = 0,
    UPLOADING = 1,
    DONE = 2,
    DONE_SUCCESS = 3,
    DONE_ERROR = 4
}

const ImageNames: string[] = [
    "damage.image1",
    "damage.image2",
    "damage.serialnumberimage",
    "key.keyimage1",
    "key.keyimage2",
    "documentimage"
];

interface SortItem {
    value: string;
    label: string;
}

enum SERVICE_SORT_KEY {
    YAROWA = "4",
    SERVICE_7000 = "5",
    JUST_EMAIL = "0"
}

const FinishBase = () => {
    let urlQuery = useUrlQuery();
    const qrCodeToken = urlQuery.get("t");

    const { t } = useTranslation();
    const { navStore, easyContactStore, authStore } = useContext(RootStoreContext);
    const { setBackbutton } = navStore;
    const [disclaimerError, setDisclaimerError] = useState(false);
    const [disclaimerModalShow, setDisclaimerModalShow] = useState(false);
    const [uploadingMessage, setUploadingMessage] = useState<string>("");
    const [uploadErrorList, setUploadErrorList] = useState<Array<UploadErrorListType>>([]);
    const [state, setState] = useState<STATE>(STATE.INIT);
    const [forwardingType, setForwardingType] = useState<CONNECTION_TYPES>(0);
    const [currentDropdownLabel, setCurrentDropdownLabel] = useState<string>(
        t("screens.finish.forward_dropdown_yarowa")
    );

    const checkbox = useRef(null);
    const history = useHistory();
    const [currentCheckbox, setCurrentCheckbox] = useState<{ checked: boolean } | null>(null);

    useEffect(() => {
        easyContactStore.setShowLogin(false);
        easyContactStore.setShowProfileAppbarButton(
            easyContactStore.notification.type === NOTIFICATION_TYPES.DAMAGE_NOTIFICATION
        );
        // only run on mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (state === STATE.UPLOADING || state === STATE.DONE || easyContactStore.sentNotificationHash !== undefined) {
            setBackbutton(null);
        }
    }, [setBackbutton, state, easyContactStore.sentNotificationHash]);

    useEffect(() => {
        checkbox.current && setCurrentCheckbox(checkbox.current);
    }, [checkbox, setCurrentCheckbox]);

    const serviceDropdownItems: SortItem[] = useMemo(() => {
        return [
            { value: SERVICE_SORT_KEY.YAROWA, label: t("screens.finish.forward_dropdown_yarowa") },
            { value: SERVICE_SORT_KEY.SERVICE_7000, label: t("screens.finish.forward_dropdown_service7000") },
            { value: SERVICE_SORT_KEY.JUST_EMAIL, label: t("screens.finish.forward_dropdown_just_email") }
        ];
    }, [t]);

    const updateSupportedThirdPartyForwardingTypes = useCallback(async () => {
        let splits: string[] = [];
        try {
            if (!easyContactStore.forwardingTypesProcessing) {
                easyContactStore.setForwardingTypesProcessing(true);
                const response = await fetch(`${NetworkConfig.supportedThirdPartyForwardingTypesUrl}`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Referer: window.location.origin
                    }
                });

                if (response.status === 200) {
                    splits = (await response.text()).split(",");
                }
            }
        } catch (err) {
            console.error("Fetch error occured: ", err);
        } finally {
            if (splits !== undefined && splits.length > 0) {
                easyContactStore.setCanForwardToService7000(
                    splits.some((p) => p === CONNECTION_TYPES.SERVICE7000.toString())
                );
                easyContactStore.setCanForwardToYarowa(splits.some((p) => p === CONNECTION_TYPES.YAROWA.toString()));
            }
            easyContactStore.setForwardingTypesProcessing(false);
        }
    }, [easyContactStore]);

    const userMustLoginAgain = useCallback(() => {
        if (easyContactStore.isLoggedIn) {
            if (easyContactStore.canForwardToYarowa) {
                easyContactStore.setCanForwardToYarowa(false);
            }
            if (easyContactStore.setCanForwardToService7000) {
                easyContactStore.setCanForwardToService7000(false);
            }
            easyContactStore.setShowLogin(true);
            easyContactStore.setIsLoggedIn(false);
        }
    }, [easyContactStore]);

    useEffect(() => {
        async function doRelogin() {
            try {
                const wasReloginSuccessful = await authStore.authClient.relogin(authStore.refresh);
                if (wasReloginSuccessful) {
                    easyContactStore.setIsLoggedIn(true);
                } else {
                    userMustLoginAgain();
                }
            } catch {
                userMustLoginAgain();
            }
        }

        if (document.cookie !== "") {
            const cookieRows = document.cookie.split("; ");
            const refreshMinLength = COOKIE_PROPERTY_REFRESH_TOKEN.length + 5;
            if (
                cookieRows.some((p) => p.startsWith(COOKIE_PROPERTY_IS_LOGGED_IN) && p.endsWith("true")) &&
                cookieRows.some((p) => p.startsWith(COOKIE_PROPERTY_REFRESH_TOKEN) && p.length > refreshMinLength)
            ) {
                if (authStore.timer === undefined && !authStore.relogin) {
                    doRelogin();
                }
            }
        }
    }, [easyContactStore.isLoggedIn, easyContactStore, authStore, userMustLoginAgain]);

    const onChangeService = useCallback(
        (serviceKey: SERVICE_SORT_KEY) => {
            const newDropdownLabel = serviceDropdownItems.find(
                (sortItem: SortItem) => sortItem.value === serviceKey
            )?.label;
            setCurrentDropdownLabel(newDropdownLabel!);
        },
        [serviceDropdownItems]
    );

    const onGetServiceDropdownItems: SortItem[] = useMemo(() => {
        updateSupportedThirdPartyForwardingTypes();

        let visibleItems = serviceDropdownItems;
        if (!easyContactStore.canForwardToYarowa) {
            visibleItems = visibleItems.filter((p) => p.value !== SERVICE_SORT_KEY.YAROWA);
        }
        if (!easyContactStore.canForwardToService7000) {
            visibleItems = visibleItems.filter((p) => p.value !== SERVICE_SORT_KEY.SERVICE_7000);
        }

        if (visibleItems.length > 0) {
            const newDropdownLabel = visibleItems[0]?.label;
            setCurrentDropdownLabel(newDropdownLabel!);
        }

        return visibleItems;
    }, [
        serviceDropdownItems,
        updateSupportedThirdPartyForwardingTypes,
        easyContactStore.canForwardToYarowa,
        easyContactStore.canForwardToService7000
    ]);

    const onClickSubmit = useCallback(async () => {
        if (currentDropdownLabel === t("screens.finish.forward_dropdown_yarowa")) {
            easyContactStore.notification.forwardingtype = CONNECTION_TYPES.YAROWA;
        } else if (currentDropdownLabel === t("screens.finish.forward_dropdown_service7000")) {
            easyContactStore.notification.forwardingtype = CONNECTION_TYPES.SERVICE7000;
        } else if (currentDropdownLabel === t("screens.finish.forward_dropdown_just_email")) {
            easyContactStore.notification.forwardingtype = CONNECTION_TYPES.NONE;
        }
        if (!easyContactStore.isLoggedIn) {
            easyContactStore.notification.forwardingtype = CONNECTION_TYPES.NONE;
            if (!currentCheckbox?.checked) {
                setDisclaimerError(true);
                return;
            }
        }

        setForwardingType(
            easyContactStore.notification.type === NOTIFICATION_TYPES.DAMAGE_NOTIFICATION &&
                easyContactStore.notification.forwardingtype
                ? easyContactStore.notification.forwardingtype
                : CONNECTION_TYPES.NONE
        );
        setState(STATE.UPLOADING);
        setUploadErrorList([]);

        setUploadingMessage(t("screens.finish.uploading_notification"));

        const omittedPaths = ImageNames.map((name) => `${name}.url`);
        let requestBody = omit(cloneDeep(toJS(easyContactStore.notification)), omittedPaths);

        requestBody["language"] = getLang();

        try {
            const access_token =
                authStore.token === undefined // TODO: Braucht es diesen Check? authStore.token wird nur beim 'login' gesetzt. Bei dem Token handelt es sich aber nicht um einen ClientCredential Token
                    ? await authStore.authClient.getECClientCredentialToken()
                    : authStore.token;

            const response = await fetch(
                `${NetworkConfig.startNotificationUrl}${qrCodeToken ? `?t=${qrCodeToken}` : ""}`,
                {
                    method: "POST",
                    body: JSON.stringify(requestBody),
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${access_token}`
                    }
                }
            );

            if (response.status === 200) {
                const token = await response.text();
                easyContactStore.setSentNotificationToken(token);

                const stringifiedNotificationPayload = JSON.stringify(requestBody);
                const notificationHash = hashCode(stringifiedNotificationPayload);
                easyContactStore.setSentNotificationHash(notificationHash);
            } else {
                setUploadErrorList([
                    {
                        text: t("error.upload.notification")
                    }
                ]);
            }
        } catch (fetchError) {
            setUploadErrorList([
                {
                    text: t("error.upload.notification")
                }
            ]);
        } finally {
            setState(STATE.DONE);
        }
    }, [
        currentCheckbox,
        t,
        easyContactStore,
        qrCodeToken,
        currentDropdownLabel,
        authStore.authClient,
        authStore.token
    ]);

    const toggleDisclaimers = useCallback(() => {
        setDisclaimerModalShow(!disclaimerModalShow);
    }, [setDisclaimerModalShow, disclaimerModalShow]);

    const disclaimer = t("screens.finish.disclaimer");
    const disclaimerCheckbox = (
        <UI.Checkbox
            name="disclaimer"
            ref={checkbox}
            label={
                <Trans i18nKey="screens.finish.accept_disclaimer">
                    A
                    <button style={{ width: "auto" }} onClick={toggleDisclaimers}>
                        {{ disclaimer }}
                    </button>
                    B
                </Trans>
            }
            inline
        />
    );

    const onCloseFinishScreen = useCallback(() => {
        easyContactStore.setSentNotificationHash(undefined);
    }, [easyContactStore]);

    return (
        <Step hideProcessProgress={state === STATE.UPLOADING || state === STATE.DONE}>
            <UI.Container className="pt-4">
                <UI.Row>
                    <UI.Col
                        style={{ minHeight: 200 }}
                        className="text-center text-center d-flex flex-column justify-content-center"
                    >
                        <BrandLogo />
                    </UI.Col>
                </UI.Row>
                {state === STATE.INIT && easyContactStore.sentNotificationHash === undefined ? (
                    <UI.Row>
                        <UI.Col md={{ span: 6, offset: 3 }} className="text-center">
                            <h3 className="mt-4 mb-4">{t("screens.finish.notification_complete")}</h3>
                            {easyContactStore.isLoggedIn && easyContactStore.showProfileAppbarButton ? (
                                <>
                                    <UI.Col md={{ span: 6, offset: 3 }} className="text-left">
                                        <p>{t("labels.submitted_to")}</p>
                                    </UI.Col>
                                    <UI.Col md={{ span: 6, offset: 3 }} className="text-center">
                                        <UI.Dropdown
                                            id="service-dropdown"
                                            items={onGetServiceDropdownItems}
                                            label={currentDropdownLabel}
                                            onSelect={onChangeService}
                                        />
                                    </UI.Col>
                                    <div style={{ height: 60 }} />
                                </>
                            ) : (
                                <>
                                    <p>{t("screens.finish.submit_confirmation")}</p>
                                    {disclaimerCheckbox}
                                    <div style={{ height: 80 }} />
                                </>
                            )}
                            {disclaimerError && (
                                <UI.Alert className="justify-content-center" variant="danger">
                                    {t("error.read_disclaimers")}
                                </UI.Alert>
                            )}
                            <UI.Button onClick={onClickSubmit} label={t("labels.submit")} />
                        </UI.Col>
                    </UI.Row>
                ) : undefined}
                {state === STATE.UPLOADING ? (
                    <UI.Row>
                        <UI.Col
                            style={{ minHeight: 200 }}
                            className="text-center text-center d-flex flex-column justify-content-center"
                        >
                            <UI.RotatingSpinner />
                            <p className="mt-4">{t("screens.finish.submitting")}</p>
                            <p>{uploadingMessage}</p>
                        </UI.Col>
                    </UI.Row>
                ) : undefined}

                {forwardingType <= 0 &&
                ((state === STATE.DONE && uploadErrorList.length === 0) ||
                    (easyContactStore.sentNotificationHash !== undefined && state !== STATE.UPLOADING)) ? (
                    <UI.Row>
                        <UI.Col md={{ span: 6, offset: 3 }} className="text-center">
                            <div>
                                <h3 className="mt-4 mb-4">{t("screens.finish.title_success")}</h3>
                                <div className="mt-4 mb-4">{t("screens.finish.message_success")}</div>
                            </div>
                            <div style={{ height: 80 }} />
                            <UI.Button
                                to={`${Route.start}${qrCodeToken ? `?t=${qrCodeToken}` : ""}`}
                                label={t("labels.close")}
                                onClick={onCloseFinishScreen}
                            />
                        </UI.Col>
                    </UI.Row>
                ) : undefined}
                {forwardingType > 0 &&
                ((state === STATE.DONE && uploadErrorList.length === 0) ||
                    (easyContactStore.sentNotificationHash !== undefined &&
                        easyContactStore.sentNotificationToken !== undefined &&
                        state !== STATE.UPLOADING))
                    ? history.push(
                          selectRoute(Route.serviceCode, {
                              code: easyContactStore.sentNotificationToken ?? "",
                              forwardingType: DIRECT_ROUTING_PREFIX.concat(String(forwardingType))
                          })
                      )
                    : undefined}

                {state === STATE.DONE && uploadErrorList.length !== 0 ? (
                    <>
                        <UI.Row>
                            <UI.Col md={{ span: 6, offset: 3 }} className="text-center">
                                <h4 className="mt-4 mb-4">{t("error.upload.title")}</h4>
                                <div className="mx-0">
                                    <ul
                                        className="text-center"
                                        style={{ listStyle: "none", padding: "0px", margin: "10px 0px 0px 0px" }}
                                    >
                                        {uploadErrorList.map((item, i) => (
                                            <li key={i} style={{ marginTop: "5px" }}>
                                                {item.text}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                <div style={{ height: 80 }} />
                                <UI.Button onClick={onClickSubmit} label={t("labels.try_again")} />
                            </UI.Col>
                        </UI.Row>
                    </>
                ) : undefined}
            </UI.Container>
            <UI.Modal
                title={t("screens.finish.disclaimer")}
                show={disclaimerModalShow}
                size="lg"
                onClose={toggleDisclaimers}
                buttons={[<UI.Button key="ok" label={t("labels.ok")} onClick={toggleDisclaimers} variant="link" />]}
            >
                <Disclaimers />
            </UI.Modal>
        </Step>
    );
};

export const Finish = Sentry.withProfiler(observer(FinishBase));
